import { useRef, useEffect, useState } from 'react';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatDate } from '../lib/dateUtils';
import { AccountingEngine } from '../services/accountingEngine';
import { AppModal } from '../components/AppModal';

export const DayBookView = ({ config, handleItemSelect, selectedIndex, setSelectedIndex }: any) => {
  const scrollRef = useRef(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);
  
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<any[] | null>(null);
  
  const vouchersData = useLiveQuery(async () => {
    if (!activeCompany) return [];
    const vouchers = (await db.vouchers
      .where('companyId').equals(activeCompany.id!)
      .filter(v => v.date >= fromDate && v.date <= toDate && !v.deleted)
      .toArray()
    ).sort((a, b) => a.date.localeCompare(b.date));
    const result = [];
    
    for (const v of vouchers) {
      const entries = await db.voucherEntries.where('voucherId').equals(v.id!).toArray();
      let particulars = 'Unknown';
      let debit = '';
      let credit = '';
      
      if (entries.length > 0) {
        const drEntries = entries.filter(e => e.type === 'Dr');
        const crEntries = entries.filter(e => e.type === 'Cr');

        // Party detection: Sales/CreditNote → first Dr; Purchase/DebitNote → first Cr; others → first entry
        let partyEntry = entries[0];
        if (v.type === 'Sales' || v.type === 'Credit Note') partyEntry = drEntries[0] || entries[0];
        else if (v.type === 'Purchase' || v.type === 'Debit Note') partyEntry = crEntries[0] || entries[0];
        else if (v.type === 'Receipt') partyEntry = crEntries[0] || entries[0];
        else if (v.type === 'Payment') partyEntry = drEntries[0] || entries[0];

        const ledger = partyEntry?.ledgerId ? await db.ledgers.get(partyEntry.ledgerId) : null;
        particulars = ledger?.name || 'Unknown';

        const totalDr = drEntries.reduce((sum, e) => sum + e.amount, 0);
        const totalCr = crEntries.reduce((sum, e) => sum + e.amount, 0);

        if (totalDr > 0) debit = totalDr.toFixed(2);
        if (totalCr > 0) credit = totalCr.toFixed(2);
      }
      
      result.push({
        id: v.id,
        date: v.date,
        particulars,
        vchType: v.type,
        vchNo: v.voucherNumber || '',
        sysId: String(v.id),
        debit,
        credit,
        name: particulars, // Added for navigation hook compatibility
        isRemoved: removedIds.has(v.id!)
      });
    }
    return result;
  }, [activeCompany, fromDate, toDate, removedIds]);

  useWheelNavigation(scrollRef, vouchersData?.length || 0, setSelectedIndex, false, () => {
    const v = vouchersData?.[selectedIndex];
    if (v && !v.isRemoved) handleItemSelect(v, selectedIndex);
  });

  const processAction = async (actionKey: string) => {
    const flatItems = vouchersData || [];
    const targetItems = selectedIds.size > 0 
      ? flatItems.filter(item => selectedIds.has(item.id))
      : (flatItems[selectedIndex] ? [flatItems[selectedIndex]] : []);

    if (actionKey === 'Space' || actionKey === ' ') {
      const item = flatItems[selectedIndex];
      if (item) {
        setSelectedIds(prev => {
          const next = new Set(prev);
          if (next.has(item.id)) next.delete(item.id);
          else next.add(item.id);
          return next;
        });
      }
    } else if (actionKey.toLowerCase() === 'a') {
      handleItemSelect({ name: 'Vouchers' });
    } else if (actionKey.toLowerCase() === 'd') {
      if (targetItems.length === 0) return;
      setPendingDelete(targetItems);
    } else if (actionKey.toLowerCase() === 'r') {
      setRemovedIds(prev => {
        const next = new Set(prev);
        for (const item of targetItems) next.add(item.id);
        return next;
      });
      setSelectedIds(new Set());
      setSelectedIndex(Math.min(flatItems.length - 1, selectedIndex + 1));
    } else if (actionKey.toLowerCase() === 'u') {
      setRemovedIds(prev => {
        const next = new Set(prev);
        if (selectedIds.size > 0) {
          for (const item of targetItems) next.delete(item.id);
        } else {
          const item = flatItems[selectedIndex];
          if (item) next.delete(item.id);
        }
        return next;
      });
      setSelectedIds(new Set());
    } else if (actionKey === 'Enter') {
      const item = flatItems[selectedIndex];
      if (item && !item.isRemoved) handleItemSelect(item, selectedIndex);
    } else if (actionKey === 'F2') {
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'F2', altKey: true }));
    } else if (actionKey === '2') {
      const item = flatItems[selectedIndex];
      if (item && !item.isRemoved) {
        handleItemSelect({ name: 'Duplicate Voucher', level: 'duplicate_voucher_form', id: item.id });
      }
    } else if (actionKey.toLowerCase() === 'q' || actionKey === 'Escape') {
      handleItemSelect({ name: 'Quit' });
    }
  };

  const executeDelete = async () => {
    const targetItems = pendingDelete || [];
    const flatItems = vouchersData || [];
    setPendingDelete(null);
    try {
      let maxAmt = 0;
      for (const v of targetItems) {
         await AccountingEngine.deleteVoucher(v.id, activeCompany!.id!);
         const amt = v.debit ? parseFloat(v.debit) : (v.credit ? parseFloat(v.credit) : 0);
         if (!isNaN(amt) && amt > maxAmt) maxAmt = amt;
      }

      let msg = targetItems.length > 1
        ? `Moved ${targetItems.length} vouchers to Bin Box.`
        : `Moved voucher ${targetItems[0].vchNo || ''} to Bin Box.`;

      if (maxAmt > 50000) msg += ` (High Value: ${maxAmt.toFixed(2)})`;
      useStore.getState().setGlobalMessage(msg);

      setSelectedIds(new Set());
      setSelectedIndex(Math.max(0, Math.min(flatItems.length - targetItems.length - 1, selectedIndex)));
    } catch (err: any) {
       alert(`Failed to soft-delete voucher: ${err.message}`);
    }
  };

  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      if (pendingDelete) return;

      // Consume all non-modifier single keys so the global navigation handler
      // doesn't intercept D/R/U/A/Q/Enter/Space meant for this view
      const viewKeys = [' ', 'Enter', 'Escape', 'F2',
        'a','d','r','u','q','i','x','2'];
      if (viewKeys.includes(e.key) || viewKeys.includes(e.key.toLowerCase())) {
        if (!e.altKey && !e.ctrlKey && !e.metaKey) e.preventDefault();
        if (!e.altKey && !e.ctrlKey && !e.metaKey) e.stopPropagation();
      }

      await processAction(e.key);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [vouchersData, selectedIndex, activeCompany, handleItemSelect, selectedIds, removedIds, setSelectedIndex, pendingDelete]);

  const daybookShortcuts: { key: string; label: string; disabled?: boolean }[] = [
    { key: 'Q', label: 'Quit' }, { key: 'Enter', label: 'Alter' }, { key: 'Space', label: 'Select' },
    { key: 'A', label: 'Add Vch' }, { key: '2', label: 'Duplicate Vch' }, { key: 'I', label: 'Insert Vch' },
    { key: 'D', label: 'Delete' }, { key: 'X', label: 'Cancel Vch' }, { key: 'R', label: 'Remove Line' },
    { key: 'U', label: 'Restore Line' },
  ];

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px]">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10">
        <span className="w-1/3 text-left">Day Book</span>
        <span className="w-1/3 text-center">{activeCompany?.name || config.companyList[0].name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="bg-white text-black flex justify-between px-2 py-1 font-bold border-b border-[#aebec8] z-10 shadow-sm">
        <span>Day Book</span><span>{formatDate(fromDate)} to {formatDate(toDate)}</span>
      </div>

      <div className="flex flex-col border-b border-[#aebec8] bg-white text-black font-bold px-2 py-1 shadow-sm">
        <div className="flex w-full">
          <div className="w-[80px]">Date</div><div className="flex-1">Particulars</div><div className="w-[120px]">Vch Type</div><div className="w-[80px] text-right">Vch No.</div><div className="w-[100px] text-right">Debit</div><div className="w-[100px] text-right">Credit</div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-white scrollbar-thin">
        {vouchersData?.map((item: any, idx: number) => {
          const isSelectedRow = selectedIndex === idx;
          const isChecked = selectedIds.has(item.id);
          
          let rowClass = 'text-black';
          if (isChecked) {
             rowClass = isSelectedRow ? 'bg-[#1b7070] text-[#ffcc00] font-bold active-row' : 'bg-[#1e8585] text-white font-semibold';
          } else if (isSelectedRow) {
             rowClass = 'bg-[#fff9c4] text-black font-semibold active-row';
          }
          
          if (item.isRemoved) {
             rowClass += ' opacity-40 italic line-through text-gray-500';
          }

          return (
            <div key={idx} onClick={() => {
                setSelectedIndex(idx);
              }} 
              onDoubleClick={() => {
                setSelectedIndex(idx);
                if (!item.isRemoved) handleItemSelect(item, idx);
              }}
              className={`flex w-full px-2 py-[2px] cursor-pointer ${rowClass}`}>
              <div className="w-[80px]">{formatDate(item.date)}</div>
              <div className="flex-1 italic font-semibold">{item.particulars}</div>
              <div className="w-[120px] font-semibold">{item.vchType}</div>
              <div className="w-[80px] text-right">{item.vchNo}</div>
              <div className="w-[100px] text-right font-semibold">{item.debit}</div>
              <div className="w-[100px] text-right font-semibold">{item.credit}</div>
            </div>
          )
        })}
        {vouchersData?.length === 0 && (
          <div className="p-4 text-center text-gray-500 italic">No vouchers found.</div>
        )}
      </div>

      <div className="flex border-t border-[#aebec8] bg-white h-[22px] w-full shadow-inner">
        {daybookShortcuts.map((sc, i) => (
          <div key={i} className={`flex-1 border-r border-[#aebec8] flex justify-between items-center px-1 cursor-pointer hover:bg-gray-100 ${sc.disabled ? 'opacity-50 pointer-events-none' : ''}`}
               onClick={() => processAction(sc.key)}
          >
            <div className="flex items-center space-x-[2px] whitespace-nowrap">
              <span className={`text-[#2a62a3] font-bold ${sc.key.length === 1 ? 'underline decoration-[#2a62a3] underline-offset-2' : ''}`}>{sc.key[0]}</span>
              {sc.key.length > 1 && <span className="text-[#2a62a3] font-bold">{sc.key.substring(1)}</span>}
              <span className="text-[#2a62a3] font-bold">:</span><span className="text-black leading-none">{sc.label}</span>
            </div>
            <span className="text-[#aebec8] text-[9px] mb-1">^</span>
          </div>
        ))}
      </div>

      <AppModal
        open={!!pendingDelete}
        title="Move to Bin Box"
        type="warning"
        message={
          pendingDelete && pendingDelete.length > 1
            ? `Move ${pendingDelete.length} vouchers to Bin Box?`
            : `Move voucher ${pendingDelete?.[0]?.vchNo || ''} to Bin Box?`
        }
        confirmLabel="Move to Bin Box"
        cancelLabel="Cancel"
        onConfirm={executeDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};
